import type { SentenceType } from 'types/types'
import { computed, reactive, type Ref, type WritableComputedRef } from 'vue'
import type { VueLogger } from 'vue-logger-plugin'

import { isFullSentenceSegmentation, isWordSegmentation } from '@/composables/segmentation'
import { PHRASE_LEVEL_SENTENCE, PHRASE_LEVEL_WORDS } from '@/composables/versePhraser'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import type { PhrasingType } from '@/util/Phrasing'
import { segmentationFromRecording } from '@/util/RecordingHelpers'
import { PHRASES, SENTENCE, WORDS } from '@/util/Tags'

/** Helper to create a writable computed that delegates to a reactive state property. */
function writableComputed<T>(getter: () => T, setter: (v: T) => void): WritableComputedRef<T> {
  return computed({ get: getter, set: setter })
}

export interface SentenceNavigationState {
  autoAdvance: boolean
  customSegmentation: boolean
  existingSentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios | null
  focusSentence: SentenceType | null
  focusSentenceIndex: number | null
  phraseLevel: number
  segmentation: number[]
}

/** Map a numeric phrase level to the internal PhrasingType tag. */
const phrasingFromLevel = (level: number): PhrasingType => {
  if (level <= PHRASE_LEVEL_SENTENCE) {
    return SENTENCE
  }
  if (level >= PHRASE_LEVEL_WORDS) {
    return WORDS
  }
  return PHRASES
}

export function useSentenceNavigation(logger: VueLogger, sentences: Ref<SentenceType[]>) {
  const state = reactive<SentenceNavigationState>({
    autoAdvance: true,
    customSegmentation: false,
    existingSentenceRecordingMetadata: null,
    focusSentence: null,
    focusSentenceIndex: null,
    phraseLevel: PHRASE_LEVEL_SENTENCE,
    segmentation: [],
  })

  // Derived from phraseLevel — used by hidden tags, RecordingControls validation, etc.
  const phrasing = computed<PhrasingType>(() => phrasingFromLevel(state.phraseLevel))

  // Computed properties
  const annotatedSentence = computed(() => {
    if (!state.focusSentence) {
      return null
    }
    return new AnnotatedSentence(state.focusSentence, null, state.segmentation)
  })

  // Standard segmentation = the default split for the mode:
  //   Read mode: 1 word per segment    Sing mode: full sentence is one segment
  const hasStandardSegmentation = (isReadMode: boolean): boolean => {
    if (state.segmentation.length === 0 || !annotatedSentence.value) {
      return false
    }
    const nWords = annotatedSentence.value.nWords()
    return isReadMode ? isWordSegmentation(state.segmentation, nWords) : isFullSentenceSegmentation(state.segmentation, nWords)
  }

  // Wraps flat segmentation into Array<Array<number>> for AnnotatedText
  const segmentations = computed<Array<Array<number>>>(() => {
    return state.segmentation.length ? [state.segmentation] : []
  })

  // Methods
  const canProceedToSentence = (targetSentenceIndex: number, isRecording = false) => {
    if (isRecording) {
      return false
    }
    return targetSentenceIndex >= 0 && targetSentenceIndex < sentences.value.length
  }

  // Shared: focus on a sentence, clearing any edit/segmentation state
  const _focusOnSentence = (sentenceIndex: number) => {
    state.focusSentence = sentences.value[sentenceIndex]
    state.focusSentenceIndex = sentenceIndex
    state.existingSentenceRecordingMetadata = null
    resetSegments()
  }

  const closeRecordOverlay = () => {
    state.focusSentence = null
    state.focusSentenceIndex = null
    state.existingSentenceRecordingMetadata = null
    resetSegments()
  }

  const handleEditRecording = (
    sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios,
    _annotatedSentence: AnnotatedSentence,
    sentenceIndex: number,
    _cache = true,
    _scroll = true,
  ) => {
    console.log('useSentenceNavigation.handleEditRecording', { sentenceId: sentences.value[sentenceIndex]?.id, sentenceIndex })
    state.existingSentenceRecordingMetadata = sentenceRecordingMetadata
    state.focusSentence = sentences.value[sentenceIndex]
    state.focusSentenceIndex = sentenceIndex
    state.segmentation = segmentationFromRecording(sentenceRecordingMetadata)
    console.log('useSentenceNavigation.handleEditRecording: focusSentence set to', state.focusSentence?.id)
    console.log('useSentenceNavigation.handleEditRecording: segmentation =', state.segmentation)
  }

  const handleSentenceClick = (_annotatedSentence: AnnotatedSentence, sentenceIndex: number, _scroll = true) => {
    logger.debug('Handling sentence click', { sentenceIndex })
    _focusOnSentence(sentenceIndex)
  }

  const proceedToSentence = (targetSentenceIndex: number) => {
    if (canProceedToSentence(targetSentenceIndex)) {
      _focusOnSentence(targetSentenceIndex)
    }
  }

  const resetSegments = () => {
    state.segmentation = []
    state.customSegmentation = false
  }

  return {
    // Computed
    annotatedSentence,
    // Writable state — consumers need to set these
    autoAdvance: writableComputed(
      () => state.autoAdvance,
      (v: boolean) => {
        state.autoAdvance = v
      },
    ),
    // Methods
    canProceedToSentence,

    closeRecordOverlay,

    customSegmentation: writableComputed(
      () => state.customSegmentation,
      (v: boolean) => {
        state.customSegmentation = v
      },
    ),

    // Read-only state — only set through composable methods
    existingSentenceRecordingMetadata: computed(() => state.existingSentenceRecordingMetadata),
    focusSentence: computed(() => state.focusSentence),
    focusSentenceIndex: computed(() => state.focusSentenceIndex),

    handleEditRecording,
    handleSentenceClick,
    hasStandardSegmentation,

    phraseLevel: writableComputed(
      () => state.phraseLevel,
      (v: number) => {
        state.phraseLevel = v
      },
    ),
    phrasing,
    proceedToSentence,
    resetSegments,

    segmentation: writableComputed(
      () => state.segmentation,
      (v: number[]) => {
        state.segmentation = v
      },
    ),
    segmentations,
  }
}
